// =============================================================================
// PrintAreaCard — single PrintArea row in the apparel sides tab
// -----------------------------------------------------------------------------
// Rendered inside a dnd-kit SortableContext. Each row exposes:
//   • Drag handle + index badge
//   • Header: displayName + base inches badge + Edit side / Edit print area
//     / Delete buttons. `displayName` and `widthIn` / `heightIn` are no
//     longer editable inline — they live in EditSideMetaModal so the row
//     stays compact (see plan §3.3).
//   • Mockup workflow toggle (MASK vs PER_VARIANT) — drives which side-
//     level controls render below.
//   • MASK only:
//       – Mockup uploader (the colorless mask the designer composes
//         variant background colours behind).
//       – "Edit print area" button → PrintAreaEditorModal.
//   • PER_VARIANT only:
//       – Banner explaining mockups + print-areas live on each variant
//         row in the matrix below.
//   • Mandatory-toggle (PATCH on change).
//
// `maxCanvasIn` was removed in the side-editor refactor — `PrintArea.width`
// is the authoritative engine-space dimension and the column is being
// deprecated. Server still accepts the field on PATCH so we can drop the
// column in a follow-up without coordinating a UI release.
// =============================================================================

import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Card, Button, Badge } from '@tkpod/ui';
import type {
  PrintAreaSummary,
  UpdatePrintAreaPayload,
} from '@/lib/api/apparel-sides';
import { MockupUploadButton } from './MockupUploadButton';

export interface PrintAreaCardProps {
  printArea: PrintAreaSummary;
  index: number;
  isUploadingMockup: boolean;
  isSavingSettings: boolean;
  isReordering: boolean;
  isDeleting?: boolean;
  /**
   * False when this is the last active side on the UPT. Server enforces
   * the same rule, but disabling the button gives merchants immediate
   * feedback (no failed round-trip).
   */
  canDelete?: boolean;
  onUploadMockup: (file: File) => void;
  onClearMockup: () => void;
  onPatch: (payload: UpdatePrintAreaPayload) => void;
  onDelete?: () => void;
  /** Opens the relative-rect Konva editor (MASK mode only). */
  onEditPrintArea: () => void;
  /** Opens the EditSideMetaModal for this side (rename + W/H). */
  onEditSideMeta: () => void;
}

export function PrintAreaCard({
  printArea,
  index,
  isUploadingMockup,
  isSavingSettings,
  isReordering,
  isDeleting,
  canDelete = true,
  onUploadMockup,
  onClearMockup,
  onPatch,
  onDelete,
  onEditPrintArea,
  onEditSideMeta,
}: PrintAreaCardProps) {
  const sortable = useSortable({ id: printArea.id });
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = sortable;

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
  };

  const handleMandatoryToggle = (next: boolean) => {
    if (isSavingSettings) return;
    onPatch({ customizationMandatory: next });
  };

  // Mockup-mode flip is the only PrintArea field whose persisted value
  // can change which workflow the merchant sees afterwards (PER_VARIANT
  // unlocks the VariantOverridesPanel for this side; MASK hides it).
  // Per-variant rows are NEVER deleted server-side on a flip — the
  // confirm prompt is purely informational and lets the merchant cancel
  // before a misclick changes the apparent surface area of the editor.
  const handleMockupModeChange = (next: 'MASK' | 'PER_VARIANT') => {
    if (isSavingSettings) return;
    if (next === printArea.mockupMode) return;

    if (next === 'MASK' && printArea.variantConfigCount > 0) {
      const ok = window.confirm(
        `Switch "${printArea.displayName}" to MASK mode?\n\n` +
          `${printArea.variantConfigCount} variant override${
            printArea.variantConfigCount === 1 ? '' : 's'
          } will stay saved but won't be used for rendering until you switch back to PER_VARIANT.`,
      );
      if (!ok) return;
    }
    onPatch({ mockupMode: next });
  };

  return (
    <Card>
      <div ref={setNodeRef} style={style} className="flex gap-4 p-4">
        {/* Drag handle + index */}
        <div className="flex flex-col items-center gap-2 pt-1">
          <button
            type="button"
            aria-label="Drag to reorder"
            className="cursor-grab text-text-tertiary hover:text-text-primary disabled:cursor-not-allowed disabled:opacity-50"
            disabled={isReordering}
            {...attributes}
            {...listeners}
          >
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 9h16.5m-16.5 6.75h16.5" />
            </svg>
          </button>
          <Badge variant="default" size="sm">
            {index + 1}
          </Badge>
        </div>

        {/* Body */}
        <div className="min-w-0 flex-1 space-y-4">
          {/* Header */}
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <h3 className="text-base font-semibold text-text-primary">
                  {printArea.displayName}
                </h3>
                {printArea.isPrimary && (
                  <Badge variant="success" size="sm">
                    Primary
                  </Badge>
                )}
                <Badge variant="default" size="sm">
                  {printArea.technique}
                </Badge>
                {printArea.sizingMode === 'FREE' && (
                  <Badge variant="info" size="sm">
                    Free draw
                  </Badge>
                )}
                {printArea.variantConfigCount > 0 && (
                  <Badge variant="warning" size="sm">
                    {printArea.variantConfigCount} variant override
                    {printArea.variantConfigCount === 1 ? '' : 's'}
                  </Badge>
                )}
              </div>
              <p className="mt-0.5 text-xs text-text-tertiary">
                {printArea.slug} ·{' '}
                {printArea.sizingMode === 'FREE' ? 'max sheet' : 'base'}{' '}
                {printArea.width.toFixed(2)} × {printArea.height.toFixed(2)} in
              </p>
            </div>
            <div className="flex flex-shrink-0 items-center gap-2">
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={onEditSideMeta}
                disabled={isReordering || isSavingSettings}
                title="Rename this side and adjust its physical print size"
              >
                <svg
                  className="mr-1.5 h-4 w-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={1.8}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M10.5 6h9.75M10.5 6a1.5 1.5 0 1 1-3 0m3 0a1.5 1.5 0 1 0-3 0M3.75 6H7.5m3 12h9.75m-9.75 0a1.5 1.5 0 0 1-3 0m3 0a1.5 1.5 0 0 0-3 0m-3.75 0H7.5m9-6h3.75m-3.75 0a1.5 1.5 0 0 1-3 0m3 0a1.5 1.5 0 0 0-3 0m-9.75 0h9.75"
                  />
                </svg>
                Edit side
              </Button>
              {/* Print-area editor only makes sense for MASK sides — the
                  per-variant rect editor for PER_VARIANT sides lives on
                  each row in the variant matrix below. */}
              {printArea.mockupMode === 'MASK' && (
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={onEditPrintArea}
                  disabled={isReordering || isSavingSettings}
                  title="Open the visual print-area editor"
                >
                  <svg className="mr-1.5 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L6.832 19.82a4.5 4.5 0 0 1-1.897 1.13l-2.685.8.8-2.685a4.5 4.5 0 0 1 1.13-1.897L16.863 4.487Zm0 0L19.5 7.125" />
                  </svg>
                  Edit print area
                </Button>
              )}
              {onDelete && (
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={onDelete}
                  disabled={!canDelete || isDeleting || isReordering}
                  title={
                    !canDelete
                      ? 'Every apparel template must have at least one side.'
                      : 'Remove this side from the template'
                  }
                  aria-label={`Remove side ${printArea.displayName}`}
                  className="text-danger-700 hover:bg-danger-50 disabled:text-text-tertiary"
                >
                  <svg
                    className="h-4 w-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={1.8}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M14.74 9 14.394 18m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0"
                    />
                  </svg>
                </Button>
              )}
            </div>
          </div>

          {/* Side-level mockup uploader — MASK only.
              In PER_VARIANT mode each variant ships its own fully-coloured
              photo (configured per-row in the matrix below), so the
              side-level uploader would never feed a render. Hiding it
              prevents merchants from shipping a stale mask asset that
              no pipeline reads. */}
          {(printArea.mockupMode ?? 'MASK') === 'MASK' && (
            <MockupUploadButton
              mockupImageUrl={printArea.mockupImageUrl}
              isUploading={isUploadingMockup}
              onUpload={onUploadMockup}
              onClear={onClearMockup}
              disabled={isReordering}
              mockupMode={printArea.mockupMode ?? 'MASK'}
            />
          )}

          {/* Mockup mode — segmented toggle */}
          <fieldset className="space-y-1.5">
            <legend className="block text-sm font-medium text-text-primary">
              Mockup mode
            </legend>
            <p className="text-xs text-text-tertiary">
              Choose how the designer renders this side across variants.
            </p>
            <div
              role="radiogroup"
              aria-label="Mockup mode"
              className="inline-flex overflow-hidden rounded-md border border-border"
            >
              <button
                type="button"
                role="radio"
                aria-checked={printArea.mockupMode === 'MASK'}
                disabled={isSavingSettings}
                onClick={() => handleMockupModeChange('MASK')}
                className={
                  'px-3 py-1.5 text-xs font-medium transition focus:outline-none focus:ring-2 focus:ring-brand-500 disabled:opacity-50 ' +
                  (printArea.mockupMode === 'MASK'
                    ? 'bg-brand-600 text-white'
                    : 'bg-surface text-text-secondary hover:bg-surface-secondary')
                }
              >
                Mask (single PNG)
              </button>
              <button
                type="button"
                role="radio"
                aria-checked={printArea.mockupMode === 'PER_VARIANT'}
                disabled={isSavingSettings}
                onClick={() => handleMockupModeChange('PER_VARIANT')}
                className={
                  'border-l border-border px-3 py-1.5 text-xs font-medium transition focus:outline-none focus:ring-2 focus:ring-brand-500 disabled:opacity-50 ' +
                  (printArea.mockupMode === 'PER_VARIANT'
                    ? 'bg-brand-600 text-white'
                    : 'bg-surface text-text-secondary hover:bg-surface-secondary')
                }
              >
                Per-variant images
              </button>
            </div>
            <p className="text-xs text-text-tertiary">
              {printArea.mockupMode === 'MASK'
                ? 'The mockup PNG above is treated as a colorless mask; the designer composes each variant\'s background color behind it.'
                : 'Each variant uses its own fully-colored mockup image, configured in the Variant matrix below.'}
            </p>
          </fieldset>

          {/* Settings row — `maxCanvasIn` was removed when the side
              editor refactor consolidated physical dimensions into
              EditSideMetaModal. The DB column persists for rollback
              safety and is being phased out in a follow-up cleanup. */}
          <div className="grid gap-4 sm:grid-cols-2">
            <label className="flex items-start gap-3">
              <input
                type="checkbox"
                className="mt-0.5 h-4 w-4 rounded border-border text-brand-600 focus:ring-brand-500"
                checked={printArea.customizationMandatory}
                disabled={isSavingSettings}
                onChange={(e) => handleMandatoryToggle(e.target.checked)}
              />
              <div className="min-w-0">
                <span className="block text-sm font-medium text-text-primary">
                  Customization required
                </span>
                <span className="block text-xs text-text-tertiary">
                  Designer blocks Create Product if this side has no design.
                </span>
              </div>
            </label>
          </div>

          {/* Variant-matrix hint — only relevant for sides that actually
              consume per-variant images (PER_VARIANT mode). */}
          {printArea.mockupMode === 'PER_VARIANT' && (
            <p className="rounded-md border border-border bg-surface-secondary px-3 py-2 text-xs text-text-tertiary">
              Per-variant images. Mockups and print-area rectangles are
              configured on each variant row below.
            </p>
          )}
        </div>
      </div>
    </Card>
  );
}
